/**
 * Catalogue row selection for the S4 definitions page (build-spec §7 S4).
 * The monthly catalogue is the 19 matrix rows in catalogue order — /metrics
 * already serves pillar → kpi_sort → type_sort (§5), so flattening preserves
 * it — followed by the matrix:false served components (Gross Profit,
 * Depreciation), labelled as such by the card. The weekly catalogue is the
 * 9-row weekly set incl. OEE. Pure selectors over typed rows (CD-7).
 */

import type { Cadence, CataloguePillar, Pillar, RegistryMetric } from '../../types/metrics'

export interface PillarGroup {
  pillar: Pillar
  metrics: RegistryMetric[]
}

function rowsForCadence(pillars: CataloguePillar[], cadence: Cadence): RegistryMetric[] {
  return pillars.flatMap((group) =>
    group.metrics.filter((metric) => metric.cadence.includes(cadence))
  )
}

/** Re-group already-ordered catalogue rows into their pillar runs, preserving
 *  §5 order — the shape the pillar sub-headings and the nav TOC both read. */
export function pillarGroups(rows: RegistryMetric[]): PillarGroup[] {
  const groups: PillarGroup[] = []
  for (const metric of rows) {
    const last = groups[groups.length - 1]
    if (last && last.pillar === metric.pillar) last.metrics.push(metric)
    else groups.push({ pillar: metric.pillar, metrics: [metric] })
  }
  return groups
}

export function monthlyCatalogueRows(pillars: CataloguePillar[]): RegistryMetric[] {
  const rows = rowsForCadence(pillars, 'monthly')
  return [...rows.filter((metric) => metric.matrix), ...rows.filter((metric) => !metric.matrix)]
}

export function weeklyCatalogueRows(pillars: CataloguePillar[]): RegistryMetric[] {
  return rowsForCadence(pillars, 'weekly')
}
